// Current California ocean sport fishing rules for every species that shows up
// in the Junior Crew Zone Map, keyed by scientific name so the Zone Map and the
// Rules page always read from one source.
//
// Rules summarise CDFW ocean sport fishing regulations for Southern California
// (south of Point Conception). Seasons, sizes and bag limits change every year
// and by management area — always confirm the current CDFW rules before keeping
// a fish.

export type SpeciesRule = {
  /** Friendly name used on the rules page. */
  common: string;
  sci: string;
  emoji: string;
  /** Open season in Southern California. */
  season: string;
  /** Minimum legal size. */
  minSize: string;
  /** Daily bag limit. */
  dailyLimit: string;
  /** One extra thing a family needs to know. */
  note: string;
};

export const CDFW_RULES_URL = "https://wildlife.ca.gov/Fishing/Ocean/Regulations";
export const CDFW_GROUNDFISH_URL = "https://wildlife.ca.gov/Fishing/Ocean/Regulations/Groundfish-Summary";
export const CDFW_LICENSE_URL = "https://wildlife.ca.gov/Licensing/Fishing";

export const CDFW_UPDATED = "Checked against the 2026 California ocean sport fishing regulations (Southern California, south of Point Conception).";

export const CDFW_DISCLAIMER =
  "These are plain-English summaries of California Department of Fish and Wildlife ocean sport fishing rules for Southern California. Seasons, sizes and bag limits change every year and by area. Always check the current CDFW regulations with an adult before you keep a fish.";

export const SPECIES_RULES: Record<string, SpeciesRule> = {
  "Amphistichus argenteus": {
    common: "Barred Surfperch",
    sci: "Amphistichus argenteus",
    emoji: "🐟",
    season: "Open all year south of Point Conception",
    minSize: "No minimum size",
    dailyLimit: "10 surfperch total, no more than 5 of any one kind",
    note: "Shiner perch do not count toward the 5-of-one-kind rule.",
  },
  "Umbrina roncador": {
    common: "Yellowfin Croaker",
    sci: "Umbrina roncador",
    emoji: "🐠",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "10 croakers per day in combination",
    note: "White seabass and corbina have their own separate limits.",
  },
  "Menticirrhus undulatus": {
    common: "California Corbina",
    sci: "Menticirrhus undulatus",
    emoji: "🐟",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "2 corbina per day",
    note: "Corbina may not be taken with nets — hook and line only.",
  },
  "Paralichthys californicus": {
    common: "California Halibut",
    sci: "Paralichthys californicus",
    emoji: "🫓",
    season: "Open all year",
    minSize: "22 inches total length",
    dailyLimit: "5 per day south of Point Sur",
    note: "Measure every halibut with a ruler — shorts must go back in the water right away.",
  },
  "Triakis semifasciata": {
    common: "Leopard Shark",
    sci: "Triakis semifasciata",
    emoji: "🦈",
    season: "Open all year",
    minSize: "36 inches total length",
    dailyLimit: "3 per day",
    note: "Leopard sharks grow slowly — most crews photograph and release them.",
  },
  "Scomber japonicus": {
    common: "Pacific (Chub) Mackerel",
    sci: "Scomber japonicus",
    emoji: "🐟",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "Counts toward the 20-fish general finfish daily limit",
    note: "Great bait fish and a great first catch from any pier.",
  },
  "Atherinopsis californiensis": {
    common: "Jacksmelt",
    sci: "Atherinopsis californiensis",
    emoji: "🐟",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "Counts toward the 20-fish general finfish daily limit",
    note: "Often caught on tiny hooks right under the pier lights.",
  },
  "Sarda chiliensis": {
    common: "Pacific Bonito",
    sci: "Sarda chiliensis",
    emoji: "🐡",
    season: "Open all year",
    minSize: "24 inches fork length or 5 pounds south of Point Conception",
    dailyLimit: "10 per day",
    note: "Only 5 of the 10 may be under the size limit north of Point Conception.",
  },
  "Semicossyphus pulcher": {
    common: "California Sheephead",
    sci: "Semicossyphus pulcher",
    emoji: "🐠",
    season: "Open all year in most of Southern California; closed during groundfish closures in some areas",
    minSize: "12 inches total length",
    dailyLimit: "2 per day",
    note: "Every sheephead starts life female and the biggest ones turn male.",
  },
  "Alopias vulpinus": {
    common: "Common Thresher Shark",
    sci: "Alopias vulpinus",
    emoji: "🦈",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "2 per day",
    note: "Far too big to lift over a pier rail — cut the line at the hook and let it swim.",
  },
  "Paralabrax clathratus": {
    common: "Kelp (Calico) Bass",
    sci: "Paralabrax clathratus",
    emoji: "🐟",
    season: "Open all year",
    minSize: "14 inches total length",
    dailyLimit: "5 per day in combination with sand bass and spotted bay bass",
    note: "Calico bass take about 5 years to reach legal size.",
  },
  "Paralabrax nebulifer": {
    common: "Barred Sand Bass",
    sci: "Paralabrax nebulifer",
    emoji: "🐟",
    season: "Open all year",
    minSize: "14 inches total length",
    dailyLimit: "5 per day in combination with kelp bass and spotted bay bass",
    note: "Summer spawning schools are protected by the size limit — measure carefully.",
  },
  "Paralabrax maculatofasciatus": {
    common: "Spotted Bay Bass",
    sci: "Paralabrax maculatofasciatus",
    emoji: "🐟",
    season: "Open all year",
    minSize: "14 inches total length",
    dailyLimit: "5 per day in combination with kelp bass and sand bass",
    note: "Most bay-caught spotties are under 14 inches, so this is a release fishery.",
  },
  "Atractoscion nobilis": {
    common: "White Seabass",
    sci: "Atractoscion nobilis",
    emoji: "🐋",
    season: "Open all year; only 1 fish per day from March 15 through June 15",
    minSize: "28 inches total length",
    dailyLimit: "3 per day outside the spring season",
    note: "The spring rule protects fish while they are spawning.",
  },
  "Seriola dorsalis": {
    common: "Yellowtail",
    sci: "Seriola dorsalis",
    emoji: "🐟",
    season: "Open all year",
    minSize: "24 inches fork length",
    dailyLimit: "10 per day (no more than 5 under 24 inches)",
    note: "Best in warm water from late spring into fall.",
  },
  "Sphyraena argentea": {
    common: "California Barracuda",
    sci: "Sphyraena argentea",
    emoji: "🐟",
    season: "Open all year",
    minSize: "28 inches total length (or 27 inches to the fork)",
    dailyLimit: "5 per day",
    note: "Sharp teeth — an adult unhooks every barracuda.",
  },
  "Thunnus orientalis": {
    common: "Pacific Bluefin Tuna",
    sci: "Thunnus orientalis",
    emoji: "🐟",
    season: "Open all year, subject to federal in-season closures",
    minSize: "No minimum size",
    dailyLimit: "2 per day, 6 in possession (federal limits can change mid-season)",
    note: "Check the current federal bluefin announcement before an offshore trip.",
  },
  "Coryphaena hippurus": {
    common: "Dorado (Mahi Mahi)",
    sci: "Coryphaena hippurus",
    emoji: "🐬",
    season: "Open all year; only shows up in warm-water years",
    minSize: "No minimum size",
    dailyLimit: "10 per day",
    note: "Fastest growing sport fish off California — a year-old fish can be 3 feet long.",
  },
  "Sebastes spp.": {
    common: "Rockfish (all Sebastes)",
    sci: "Sebastes spp.",
    emoji: "🪨",
    season: "Season and depth limits change by groundfish management area — check the CDFW groundfish page before every trip",
    minSize: "No minimum size for most rockfish",
    dailyLimit: "10 per day inside the 20-fish Rockfish-Cabezon-Greenling limit",
    note: "Bring a descending device — it sends deep-water rockfish back down alive.",
  },
  "Ophiodon elongatus": {
    common: "Lingcod",
    sci: "Ophiodon elongatus",
    emoji: "🐊",
    season: "Open during the groundfish season for your management area",
    minSize: "22 inches total length in Southern California",
    dailyLimit: "2 per day",
    note: "Lingcod count separately from the rockfish limit.",
  },
  "Girella nigricans": {
    common: "Opaleye",
    sci: "Girella nigricans",
    emoji: "🟢",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "Counts toward the 20-fish general finfish daily limit",
    note: "Vegetarian fish — frozen peas and moss are real baits.",
  },
  "Anisotremus davidsonii": {
    common: "Sargo",
    sci: "Anisotremus davidsonii",
    emoji: "🐟",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "Counts toward the 20-fish general finfish daily limit",
    note: "Sargo grunt with their throat teeth when you land them.",
  },
  "Cymatogaster aggregata": {
    common: "Shiner Perch",
    sci: "Cymatogaster aggregata",
    emoji: "🐠",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "20 shiner perch per day, on top of the 10-surfperch limit",
    note: "The classic first fish for a kid on a bay dock.",
  },
  "Myliobatis californica": {
    common: "Bat Ray",
    sci: "Myliobatis californica",
    emoji: "🥏",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "No daily limit (general 20-fish finfish rule does not apply to rays)",
    note: "The tail has a spine — an adult handles every ray.",
  },
  "Urobatis halleri": {
    common: "Round Stingray",
    sci: "Urobatis halleri",
    emoji: "🥏",
    season: "Open all year",
    minSize: "No minimum size",
    dailyLimit: "No daily limit",
    note: "Shuffle your feet in the shallows so you never step on one.",
  },
};

export function ruleFor(sci: string): SpeciesRule | undefined {
  return SPECIES_RULES[sci];
}

/** One line a kid can read on the Zone Map: size limit + daily limit. */
export function keeperSummary(sci: string, fallback: string): string {
  const rule = ruleFor(sci);
  if (!rule) return fallback;
  return `${rule.minSize} · ${rule.dailyLimit}`;
}

/** Licence and access rules families ask about most. */
export const LICENSE_RULES: { title: string; body: string }[] = [
  {
    title: "Kids under 16 fish free",
    body: "Anyone under 16 does not need a California sport fishing licence in the ocean. Everyone 16 and older needs one, plus an ocean enhancement validation south of Point Arguello.",
  },
  {
    title: "Public piers need no licence",
    body: "On most public ocean piers and jetties in California, anglers of any age can fish without a licence. All size, season and bag limits still apply.",
  },
  {
    title: "Marine Protected Areas",
    body: "Some of the best-looking spots are MPAs where fishing is limited or banned. Check the CDFW MPA map for your beach before you cast.",
  },
  {
    title: "One rod, two hooks",
    body: "From a public pier you may use two rods. Elsewhere in ocean waters, finfish rules limit you to hook-and-line gear as described in the current regulations.",
  },
];
